import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Table, Row, Col, Alert } from 'react-bootstrap';
import cashSessionService from '../../services/cashSessionService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const CashSessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (value) => {
    return `$${toNumber(value).toFixed(2)}`;
  };

  useEffect(() => {
    loadSession();
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await cashSessionService.getById(id);

      if (response.success) {
        setSession(response.data.session);
        setResumen(response.data.resumen);
        setMovimientos(response.data.movimientos || []);
      } else {
        setError('No se pudo cargar la sesión');
      }
    } catch (err) {
      setError('Error al cargar la sesión de caja');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTipoMovimientoBadge = (tipo) => {
    const tipos = {
      venta:   <Badge bg="success">Venta</Badge>,
      retiro:  <Badge bg="danger">Retiro</Badge>,
      ingreso: <Badge bg="primary">Ingreso</Badge>,
    };
    return tipos[tipo] || <Badge bg="secondary">{tipo}</Badge>;
  };

  const getDiferenciaBadge = (diferencia) => {
    const diff = toNumber(diferencia);
    if (diff === 0) return <Badge bg="success">Cuadrado</Badge>;
    if (diff > 0)   return <Badge bg="warning" text="dark">Sobrante +{formatCurrency(diff)}</Badge>;
    return <Badge bg="danger">Faltante {formatCurrency(diff)}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/cash/history')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver al Historial
        </Button>
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="container-fluid mt-4">

      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/cash/history')}
            className="me-3"
          >
            <i className="bi bi-arrow-left me-1"></i>
            Volver
          </Button>
          <span className="h4">
            <i className="bi bi-cash-register me-2"></i>
            Sesión #{session.id}
          </span>
        </div>
        <div>
          {session.estado === 'abierta'
            ? <Badge bg="success" className="fs-6">Abierta</Badge>
            : <Badge bg="secondary" className="fs-6">Cerrada</Badge>
          }
        </div>
      </div>

      {/* Info General */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="fw-bold">
              <i className="bi bi-info-circle me-2"></i>
              Información de la Sesión
            </Card.Header>
            <Card.Body>
              <table className="table table-borderless table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted fw-semibold" width="40%">Caja:</td>
                    <td>{session.cash_register?.nombre || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Punto de Venta:</td>
                    <td>{session.punto_venta?.nombre || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Cajero:</td>
                    <td>
                      {session.nombre_cajero
                        ? <><i className="bi bi-person me-1"></i>{session.nombre_cajero}</>
                        : <span className="text-muted fst-italic">No registrado</span>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Apertura:</td>
                    <td>{formatDate(session.fecha_apertura)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Cierre:</td>
                    <td>{formatDate(session.fecha_cierre)}</td>
                  </tr>
                  {session.observaciones_apertura && (
                    <tr>
                      <td className="text-muted fw-semibold">Obs. Apertura:</td>
                      <td>{session.observaciones_apertura}</td>
                    </tr>
                  )}
                  {session.observaciones_cierre && (
                    <tr>
                      <td className="text-muted fw-semibold">Obs. Cierre:</td>
                      <td>{session.observaciones_cierre}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="fw-bold">
              <i className="bi bi-calculator me-2"></i>
              Resumen de Caja
            </Card.Header>
            <Card.Body>
              <table className="table table-borderless table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted fw-semibold" width="50%">Monto Inicial:</td>
                    <td className="text-end fw-semibold">{formatCurrency(session.monto_inicial)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Ventas Efectivo:</td>
                    <td className="text-end text-success fw-semibold">{formatCurrency(session.monto_ventas_efectivo)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Ingresos:</td>
                    <td className="text-end text-primary fw-semibold">{formatCurrency(session.monto_ingresos)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Retiros:</td>
                    <td className="text-end text-danger fw-semibold">-{formatCurrency(session.monto_retiros)}</td>
                  </tr>
                  <tr className="border-top">
                    <td className="fw-bold">Monto Esperado:</td>
                    <td className="text-end fw-bold">
                      {formatCurrency(
                        session.monto_esperado ?? (
                          toNumber(session.monto_inicial) +
                          toNumber(session.monto_ventas_efectivo) +
                          toNumber(session.monto_ingresos) -
                          toNumber(session.monto_retiros)
                        )
                      )}
                    </td>
                  </tr>
                  {session.monto_real !== null && (
                    <>
                      <tr>
                        <td className="fw-bold">Monto Real:</td>
                        <td className="text-end fw-bold">{formatCurrency(session.monto_real)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Diferencia:</td>
                        <td className="text-end">{getDiferenciaBadge(session.diferencia)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Movimientos */}
      <Card>
        <Card.Header className="fw-bold">
          <i className="bi bi-list-ul me-2"></i>
          Movimientos ({movimientos.length})
        </Card.Header>
        <Card.Body className="p-0">
          {movimientos.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4"></i>
              <p className="mt-2">No hay movimientos registrados</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Tipo</th>
                  <th>Concepto</th>
                  <th>Fecha</th>
                  <th className="text-end">Monto</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map((mov) => (
                  <tr key={mov.id}>
                    <td>{getTipoMovimientoBadge(mov.tipo)}</td>
                    <td>
                      {mov.concepto || (mov.sale ? `Venta #${mov.sale.id}` : '-')}
                      {mov.descripcion && (
                        <div className="text-muted small">{mov.descripcion}</div>
                      )}
                    </td>
                    <td className="text-muted small">{formatDate(mov.created_at)}</td>
                    <td className="text-end fw-semibold">
                      <span className={
                        mov.tipo === 'retiro' ? 'text-danger' :
                        mov.tipo === 'ingreso' ? 'text-primary' : 'text-success'
                      }>
                        {mov.tipo === 'retiro' ? '-' : '+'}{formatCurrency(mov.monto)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

    </div>
  );
};

export default CashSessionDetail;